import React, { useMemo } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { Range } from 'react-range';
import { Balloon } from '../../../../interfaces/Ballooning';

interface CustomSliderProps {
    balloon: Balloon;
    value: number;
    source: string;
    disabled: boolean;
    onChange: (value: number) => void;
    onCommit: (value: number) => void;
    unit?: string;
    error?: boolean;
}

const CustomSlider: React.FC<CustomSliderProps> = ({
    balloon,
    value,
    source,
    disabled,
    onChange,
    onCommit,
    unit,
    error,
}) => {
    const rawMin = Number(balloon.lower_control_limit);
    const rawMax = Number(balloon.upper_control_limit);
    const min = Math.min(rawMin, rawMax);
    const max = Math.max(rawMin, rawMax);
    const nominal = Number(balloon.nominal);
    const precision = unit === 'inch' ? 3 : 4;
    const isConfirmed = source === 'result';
    const isError = !!error;

    const epsilon = useMemo(() => Math.pow(10, -precision), [precision]);
    const hasZeroSpan = max - min === 0;
    const numericValue = typeof value === 'number' ? value : Number(value ?? 0);
    // react-range throws a RangeError when a value lies outside [min, max],
    // which unmounts the whole hand-quality section. The value can
    // legitimately fall outside the control band: an out-of-tolerance
    // reading, or a nominal that sits outside its own limits (all-negative
    // tolerance like 0.197 -0.0002/-0.0005) used as the default before any
    // entry. Extend the track to contain the value instead of crashing; the
    // thumb then renders past the band.
    const baseMin = hasZeroSpan ? min - epsilon : min;
    const baseMax = hasZeroSpan ? max + epsilon : max;
    const sliderMin = Number.isFinite(numericValue) ? Math.min(baseMin, numericValue) : baseMin;
    const sliderMax = Number.isFinite(numericValue) ? Math.max(baseMax, numericValue) : baseMax;
    const sliderSpan = sliderMax - sliderMin;

    const marks = useMemo(() => {
        const set = new Set<number>();
        const decInterval = Number(balloon.decremental_interval) || 0;
        const incInterval = Number(balloon.incremental_interval) || 0;

        set.add(Number(nominal.toFixed(precision)));

        if (decInterval > 0) {
            for (let v = nominal - decInterval; v >= min; v -= decInterval) {
                set.add(Number(v.toFixed(precision)));
            }
        } else {
            set.add(Number(min.toFixed(precision)));
        }

        if (incInterval > 0) {
            for (let v = nominal + incInterval; v <= max; v += incInterval) {
                set.add(Number(v.toFixed(precision)));
            }
        } else {
            set.add(Number(max.toFixed(precision)));
        }

        return Array.from(set).sort((a, b) => a - b);
    }, [balloon, min, max, nominal, precision]);

    const gradientPercent = sliderSpan === 0
        ? 50
        : Math.max(0, Math.min(100, ((nominal - sliderMin) / sliderSpan) * 100));

    const snapToClosest = (val: number): number => {
        return marks.reduce((prev, curr) =>
            Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev
        );
    };


    return (
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <div style={{ flex: 1, padding: '0 12px' }}>
                <Range
                    step={0.0001}
                    min={sliderMin}
                    max={sliderMax}
                    values={[Number.isFinite(numericValue) ? numericValue : sliderMin]}
                    onChange={(vals) => {
                        if (!disabled) {
                            const snapped = snapToClosest(vals[0]);
                            onChange(snapped);
                        }
                    }}
                    onFinalChange={(vals) => {
                        if (!disabled) {
                            const snapped = snapToClosest(vals[0]);
                            onChange(snapped);
                            onCommit(snapped);
                        }
                    }}
                    renderTrack={({ props, children }) => {
                        const { onMouseDown, onTouchStart, onPointerDown, ref, ...rest } = props as any;
                        return (
                            <div
                                {...rest}
                                ref={ref}
                                style={{
                                    ...props.style,
                                    height: '14px',
                                    width: '100%',
                                    borderRadius: '7px',
                                    background: `linear-gradient(to right, #ffe5b4 0%, #52c41a ${gradientPercent}%, #ffe5b4 100%)`,
                                }}
                            >
                                {children}
                                <div style={{ position: 'relative', top: 16 }}>
                                    {marks.map((m, i) => {
                                        const left = sliderSpan === 0 ? 50 : ((m - sliderMin) / sliderSpan) * 100;
                                        const delta = (max - min) * 0.01;
                                        const isThumbCovering = Math.abs(m - numericValue) <= delta;
                                        return (
                                            <div
                                                key={i}
                                                style={{
                                                    position: 'absolute',
                                                    left: `${left}%`,
                                                    transform: 'translateX(-50%)',
                                                    fontSize: 10,
                                                    color: m === nominal ? '#000' : '#ccc',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {m === nominal
                                                    ? isThumbCovering
                                                        ? '|'
                                                        : m.toFixed(precision)
                                                    : '|'}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    }}
                    renderThumb={({ props, isDragged }) => (
                        <div
                            {...props}
                            style={{
                                ...props.style,
                                height: isDragged ? '56px' : '28px',
                                bottom: isDragged ? '-32px' : '-16px',
                                //width: '80px',
                                borderRadius: '6px',
                                backgroundColor: isError
                                    ? '#ff4d4f'
                                    : isConfirmed
                                        ? 'rgba(20, 195, 67)'
                                        : '#fff',
                                border: isError
                                    ? '2px solid #ff4d4f'
                                    : isConfirmed
                                        ? '2px solid #0d7a9e'
                                        : '1px solid #000',
                                color: isError ? 'white' : (isConfirmed ? 'white' : 'black'),
                                fontWeight: 800,
                                fontSize: '12px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                paddingTop: '6px',
                                paddingBottom: '6px',
                                paddingLeft: '4px',
                                paddingRight: '4px',
                                boxShadow: '0 0 4px rgba(0, 0, 0, 0.25)',
                                cursor: 'grab',
                                transform: 'translateY(-10px)',
                                zIndex: 5,

                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    gap: '1px',
                                }}
                            >

                                {numericValue.toFixed(precision)}

                               
                            </div>
                        </div>
                    )}


                />
            </div>
        </div>
    );
};

export default CustomSlider;
